<template>
  <div>
    <!-- Filters Section -->
    <b-row class="mb-3">
      <b-col
        lg="3"
        md="4"
        cols="12"
      >
        <b-form-group
          :label="$t('hospital.patient-name')"
          label-for="patient-name-filter"
        >
          <b-form-input
            id="patient-name-filter"
            v-model="patientNameFilter"
            :placeholder="$t('hospital.patient-name')"
            @keyup.enter="getDoctorInvoices"
          />
        </b-form-group>
      </b-col>
      <b-col
        lg="3"
        md="4"
        cols="12"
      >
        <b-form-group
          :label="$t('common.invoice-number')"
          label-for="invoice-no-filter"
        >
          <b-form-input
            id="invoice-no-filter"
            v-model="invoiceNoFilter"
            :placeholder="$t('common.invoice-number')"
            @keyup.enter="getDoctorInvoices"
          />
        </b-form-group>
      </b-col>
      <b-col
        lg="3"
        md="4"
        cols="12"
      >
        <b-form-group
          :label="$t('common.from')"
          label-for="from-date"
        >
          <datepicker
            id="from-date"
            v-model="fromDate"
            :calendar-button="true"
            :clear-button="true"
            :bootstrap-styling="true"
            class="datebtn2"
            placeholder="YYYY-MM-DD"
          />
        </b-form-group>
      </b-col>
      <b-col
        lg="3"
        md="4"
        cols="12"
      >
        <b-form-group
          :label="$t('common.to')"
          label-for="to-date"
        >
          <datepicker
            id="to-date"
            v-model="toDate"
            :calendar-button="true"
            :clear-button="true"
            :bootstrap-styling="true"
            class="datebtn2"
            placeholder="YYYY-MM-DD"
          />
        </b-form-group>
      </b-col>
      <b-col
        lg="3"
        md="12"
        cols="12"
        class="d-flex align-items-end mb-2 mb-lg-0"
      >
        <b-button
          variant="primary"
          class="mr-2"
          @click="getDoctorInvoices"
        >
          {{ $t("common.search") }}
        </b-button>
        <b-button
          variant="secondary"
          @click="clearFilters"
        >
          {{ $t("common.clear") }}
        </b-button>
      </b-col>
    </b-row>

    <!-- Status Summary Cards -->
    <b-row class="mb-3">
      <b-col
        md="4"
        cols="12"
      >
        <b-card class="text-center">
          <h6 class="text-muted mb-1">
            {{ $t('accounting.pending-amount') || 'المبلغ المعلق' }}
          </h6>
          <h4 class="text-warning mb-0">
            {{ formatIQDCurrency(statusSummary.pending) }}
          </h4>
        </b-card>
      </b-col>
      <b-col
        md="4"
        cols="12"
      >
        <b-card class="text-center">
          <h6 class="text-muted mb-1">
            {{ $t('accounting.paid-amount') || 'المبلغ المدفوع' }}
          </h6>
          <h4 class="text-success mb-0">
            {{ formatIQDCurrency(statusSummary.paid) }}
          </h4>
        </b-card>
      </b-col>
      <b-col
        md="4"
        cols="12"
      >
        <b-card class="text-center">
          <h6 class="text-muted mb-1">
            {{ $t('common.total') }}
          </h6>
          <h4 class="text-primary mb-0">
            {{ formatIQDCurrency(statusSummary.total) }}
          </h4>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tabs Structure -->
    <b-tabs
      v-model="activeTab"
      card
      @input="onTabChange"
    >
      <!-- Pending Tab -->
      <b-tab
        :title="`${$t('accounting.pending') || 'معلق'} (${formatIQDCurrency(statusSummary.pending)})`"
      >
        <b-card-text>
          <!-- Action Buttons -->
          <div
            v-if="activeTab === 0"
            class="mb-3 d-flex justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center gap-2">
              <b-button
                variant="success"
                :disabled="selectedInvoices.length === 0 || bulkUpdating"
                @click="bulkUpdateStatusToPaid"
              >
                <b-spinner
                  v-if="bulkUpdating"
                  small
                  class="mr-1"
                />
                <feather-icon
                  v-else
                  icon="CheckIcon"
                  size="14"
                  class="mr-1"
                />
                {{ $t('accounting.mark-as-paid') || 'تحديد كمدفوع' }}

                {{ selectedInvoices.length }}

              </b-button>
              <b-button
                variant="info"
                @click="openPrintModal('pending')"
              >
                <feather-icon
                  icon="PrinterIcon"
                  size="14"
                  class="mr-1"
                />
                {{ $t('common.print') || 'طباعة' }}
              </b-button>
            </div>
            <div
              v-if="selectedInvoices.length > 0"
              class="d-flex align-items-center"
            >
              <span class="mr-2">
                <strong>{{ $t('common.total') }}:</strong>
              </span>
              <span class="font-weight-bold text-primary">
                {{ formatIQDCurrency(selectedTotalPrice) }}
              </span>
            </div>
          </div>

          <!-- Table Display -->
          <b-table
            v-if="!loadingInvoices"
            :items="invoicesData"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="No matching invoices found"
            class="position-relative"
          >
            <template #head(select)>
              <b-form-checkbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="toggleSelectAll"
              />
            </template>
            <template #cell(select)="data">
              <b-form-checkbox
                :checked="isInvoiceSelected(data.item)"
                @change="toggleInvoiceSelection(data.item)"
              />
            </template>
            <template #cell(invoice_no)="data">
              <span class="font-weight-bold">{{
                data.item.invoice_no || "-"
              }}</span>
            </template>
            <template #cell(patient_name)="data">
              <span>{{
                getPatientName(data.item)
              }}</span>
            </template>
            <template #cell(type)="data">
              <span class="text-nowrap">{{
                formatInvoiceServiceOrType(data.item)
              }}</span>
            </template>
            <template #cell(amount)="data">
              <span class="text-nowrap font-weight-bold">
                {{ formatIQDCurrency(data.item.amount || 0) }}
              </span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item.status)">
                {{ data.item.status || "-" }}
              </b-badge>
            </template>
            <template #cell(date)="data">
              <span class="text-nowrap">{{
                formatDateDisplay(data.item.date || data.item.created_at)
              }}</span>
            </template>

          </b-table>

          <!-- Loading Spinner -->
          <div
            v-if="loadingInvoices"
            class="text-center p-4"
          >
            <b-spinner label="Loading..." />
            <p class="mt-2">
              {{ $t("common.loading") }}
            </p>
          </div>

          <!-- Pagination -->
          <div class="mx-2 mb-2 mt-3">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{ $t("common.showing") }}
                  {{ invoicesData.length > 0 ? (page - 1) * take + 1 : 0 }}
                  {{ $t("common.to") }}
                  {{ Math.min(page * take, invoicesCount) }}
                  {{ $t("common.of") }}
                  {{ invoicesCount }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="invoicesCount"
                  :per-page="take"
                  @change="handlePageChange"
                />
              </b-col>
            </b-row>
          </div>
        </b-card-text>
      </b-tab>

      <!-- Paid Tab -->
      <b-tab
        :title="`${$t('accounting.paid') || 'مدفوع'} (${formatIQDCurrency(statusSummary.paid)})`"
      >
        <b-card-text>
          <!-- Print Button -->
          <div class="mb-3 d-flex justify-content-end">
            <b-button
              variant="info"
              @click="openPrintModal('paid')"
            >
              <feather-icon
                icon="PrinterIcon"
                size="14"
                class="mr-1"
              />
              {{ $t('common.print') || 'طباعة' }}
            </b-button>
          </div>

          <!-- Table Display -->
          <b-table
            v-if="!loadingInvoices"
            :items="invoicesData"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="No matching invoices found"
            class="position-relative"
          >
            <template #cell(invoice_no)="data">
              <span class="font-weight-bold">{{
                data.item.invoice_no || "-"
              }}</span>
            </template>
            <template #cell(patient_name)="data">
              <span>{{
                getPatientName(data.item)
              }}</span>
            </template>
            <template #cell(type)="data">
              <span class="text-nowrap">{{
                formatInvoiceServiceOrType(data.item)
              }}</span>
            </template>
            <template #cell(amount)="data">
              <span class="text-nowrap font-weight-bold">
                {{ formatIQDCurrency(data.item.amount || 0) }}
              </span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item.status)">
                {{ data.item.status || "-" }}
              </b-badge>
            </template>
            <template #cell(date)="data">
              <span class="text-nowrap">{{
                formatDateDisplay(data.item.date || data.item.created_at)
              }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button
                variant="primary"
                size="sm"
                :disabled="isUpdateDisabled(data.item)"
                @click="openEditDoctorAmountModal(data.item)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                />
                {{ $t("accounting.update-doctor-amount") }}
              </b-button>
            </template>
          </b-table>

          <!-- Loading Spinner -->
          <div
            v-if="loadingInvoices"
            class="text-center p-4"
          >
            <b-spinner label="Loading..." />
            <p class="mt-2">
              {{ $t("common.loading") }}
            </p>
          </div>

          <!-- Pagination -->
          <div class="mx-2 mb-2 mt-3">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{ $t("common.showing") }}
                  {{ invoicesData.length > 0 ? (page - 1) * take + 1 : 0 }}
                  {{ $t("common.to") }}
                  {{ Math.min(page * take, invoicesCount) }}
                  {{ $t("common.of") }}
                  {{ invoicesCount }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="invoicesCount"
                  :per-page="take"
                  @change="handlePageChange"
                />
              </b-col>
            </b-row>
          </div>
        </b-card-text>
      </b-tab>
    </b-tabs>

    <!-- Print Modal -->
    <b-modal
      id="modal-print-debt-statement"
      :title="$t('common.print') || 'طباعة'"
      size="xl"
      :no-close-on-backdrop="true"
      centered
      @hide="closePrintModal"
      @shown="handlePrintModalShown"
    >
      <DoctorDebtStatement
        ref="debtStatementPrint"
        :data="invoicesToPrint.length > 0 ? invoicesToPrint : invoicesData"
        :doctor-name="doctorName"
        :status-type="printModalStatusType"
        :from-date="fromDate ? formatDateForAPI(fromDate) : ''"
        :to-date="toDate ? formatDateForAPI(toDate) : ''"
        :auto-print="autoPrintAfterUpdate"
        @print-started="handlePrintStarted"
        @print-completed="handlePrintCompleted"
      />
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closePrintModal"
        >
          {{ $t("common.close") || "إغلاق" }}
        </b-button>
      </template>
    </b-modal>

    <!-- Edit Doctor Amount Modal -->
    <b-modal
      :id="modalId"
      :title="$t('accounting.edit-doctor-amount')"
      size="md"
      :no-close-on-backdrop="true"
      centered
      @hide="closeEditDoctorAmountModal"
    >
      <div v-if="selectedInvoice">
        <b-form-group :label="$t('common.doctor-name')">
          <span class="font-weight-bold">{{
            doctorName || "-"
          }}</span>
        </b-form-group>
        <b-form-group :label="$t('accounting.current-amount')">
          <span class="font-weight-bold text-primary">
            {{ formatIQDCurrency(selectedInvoice.doctor_amount || 0) }}
          </span>
        </b-form-group>
        <b-form-group
          :label="$t('accounting.new-doctor-amount')"
          :invalid-feedback="$t('validation.required')"
        >
          <b-form-input
            v-model.number="doctorAmountForm.doctor_amount"
            type="number"
            :placeholder="$t('accounting.enter-doctor-amount')"
            :min="0"
            step="0.01"
            required
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closeEditDoctorAmountModal"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="editingDoctorAmount"
          @click="updateDoctorAmount"
        >
          <b-spinner
            v-if="editingDoctorAmount"
            small
            class="mr-1"
          />
          {{ $t("common.update") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BTable,
  BButton,
  BModal,
  BSpinner,
  BPagination,
  BBadge,
  BTabs,
  BTab,
  BCardText,
  BCard,
  BFormCheckbox,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DoctorDebtStatement from '@/components/printables/DoctorDebtStatement.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BTable,
    BButton,
    BModal,
    BSpinner,
    BPagination,
    BBadge,
    BTabs,
    BTab,
    BCardText,
    BCard,
    BFormCheckbox,
    Datepicker,
    ToastificationContent,
    DoctorDebtStatement,
  },
  props: {
    doctorId: {
      type: String,
      required: true,
    },
    doctorName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      invoicesData: [],
      invoicesCount: 0,
      loadingInvoices: false,
      page: 1,
      take: 10,
      patientNameFilter: '',
      invoiceNoFilter: '',
      fromDate: '',
      toDate: '',
      selectedInvoice: null,
      doctorAmountForm: {
        doctor_amount: 0,
      },
      editingDoctorAmount: false,
      statusSummary: {
        paid: 0,
        pending: 0,
        total: 0,
      },
      activeTab: 0,
      selectedInvoices: [],
      bulkUpdating: false,
      showPrintModal: false,
      printModalStatusType: 'pending',
      invoicesToPrint: [],
      autoPrintAfterUpdate: false,
    }
  },
  computed: {
    tableColumns() {
      const columns = []
      // Add select column only for pending tab
      if (this.activeTab === 0) {
        columns.push({
          key: 'select',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
        })
      }
      columns.push(
        {
          key: 'invoice_no',
          label: this.$t('common.invoice-number'),
          sortable: true,
        },
        {
          key: 'patient_name',
          label: this.$t('hospital.patient-name'),
          sortable: true,
        },
        {
          key: 'type',
          label: this.$t('common.type'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('common.amount'),
          sortable: true,
        },
        {
          key: 'status',
          label: this.$t('common.status'),
          sortable: true,
        },
        {
          key: 'date',
          label: this.$t('common.date'),
          sortable: true,
        },
      )
      return columns
    },
    selectedTotalPrice() {
      return this.selectedInvoices.reduce(
        (sum, invoice) => sum + parseFloat(invoice.amount || 0),
        0,
      )
    },
    isAllSelected() {
      return (
        this.invoicesData.length > 0
        && this.selectedInvoices.length === this.invoicesData.length
      )
    },
    isIndeterminate() {
      return (
        this.selectedInvoices.length > 0
        && this.selectedInvoices.length < this.invoicesData.length
      )
    },
    modalId() {
      if (this.selectedInvoice && this.selectedInvoice.id) {
        return `modal-edit-doctor-amount-${this.selectedInvoice.id}`
      }
      return 'modal-edit-doctor-amount-default'
    },
  },
  mounted() {
    this.getDoctorStatusSummary()
    this.getDoctorInvoices()
  },
  methods: {
    async getDoctorStatusSummary() {
      if (!this.doctorId) return

      try {
        const response = await this.$http.get(
          `/invoices/doctor-dept-status/${this.doctorId}`,
        )
        if (response.data) {
          this.statusSummary = {
            paid: response.data.paid || 0,
            pending: response.data.pending || 0,
            total: response.data.total || 0,
          }
        }
      } catch (error) {
        console.error('Error fetching doctor status summary:', error)
        this.statusSummary = { paid: 0, pending: 0, total: 0 }
      }
    },
    async getDoctorInvoices() {
      if (!this.doctorId) return

      this.loadingInvoices = true
      try {
        // Determine status filter based on active tab
        const statusFilter = this.activeTab === 0 ? 'doctor_pending' : 'paid'
        let url = `/invoices?filter[doctor_dept_id]=${this.doctorId}&filter[status]=${statusFilter}&page=${this.page}&take=${this.take}&sort=-id&include=service,surgery,patientCard,doctor,invoiceItems`

        // Add invoice number filter
        if (this.invoiceNoFilter && this.invoiceNoFilter.trim()) {
          url += `&filter[invoice_no]=${encodeURIComponent(this.invoiceNoFilter.trim())}`
        }

        // Add patient name filter
        if (this.patientNameFilter && this.patientNameFilter.trim()) {
          // First, fetch patient cards by name
          try {
            const patientName = encodeURIComponent(
              this.patientNameFilter.trim(),
            )
            await this.getList(
              `/patientCard?filter[patient.name]=${patientName}&take=1000`,
            )
            const patientCards = this.all_data && this.all_data.data
              ? this.all_data.data
              : []
            if (patientCards.length > 0) {
              const cardIds = patientCards.map(card => card.id).join(',')
              url += `&filter[patient_card_id]=${cardIds}`
            } else {
              // No patient cards found, return empty results
              this.invoicesData = []
              this.invoicesCount = 0
              this.loadingInvoices = false
              return
            }
          } catch (error) {
            console.error('Error fetching patient cards:', error)
          }
        }

        // Add date filters
        if (this.fromDate) {
          url += `&filter[date][gte]=${this.formatDateForAPI(this.fromDate)}`
        }
        if (this.toDate) {
          url += `&filter[date][lte]=${this.formatDateForAPI(this.toDate)}`
        }

        await this.getList(url)
        this.invoicesData = this.all_data.data || []
        this.invoicesCount = this.all_data.total || 0
      } catch (error) {
        console.error('Error fetching doctor invoices:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('common.error-loading-invoices'),
          },
        })
        this.invoicesData = []
        this.invoicesCount = 0
      } finally {
        this.loadingInvoices = false
      }
    },

    clearFilters() {
      this.patientNameFilter = ''
      this.invoiceNoFilter = ''
      this.fromDate = ''
      this.toDate = ''
      this.page = 1
      this.getDoctorInvoices()
    },

    handlePageChange(page) {
      this.page = page
      this.getDoctorInvoices()
      // Clear selection when paginating
      this.clearSelection()
    },

    formatIQDCurrency(value) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'IQD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      return formatter.format(value)
    },

    formatDateDisplay(date) {
      if (!date) return '-'
      const newDate = new Date(date)
      const year = newDate.getFullYear()
      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const day = String(newDate.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatDateForAPI(date) {
      if (!date) return ''
      const newDate = new Date(date)
      const year = newDate.getFullYear()
      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const day = String(newDate.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatInvoiceType(type) {
      const typeMap = {
        invoice_out_clinic: this.$t('reception.out-clinic-invoices'),
        invoice_nicu: this.$t('incubator.nicu-invoices'),
        invoice_insurance_surgery: this.$t('surgery.surgery-invoices'),
        invoice_emergency: this.$t('emergency.emergency-invoices'),
        invoice_patient_card_test: this.$t('lab.test-invoices'),
        refund: this.$t('accounting.refund-invoice'),
      }
      return typeMap[type] || type
    },
    formatInvoiceServiceOrType(invoice) {
      const serviceTitle = this.getNestedProperty(invoice, 'service.title', '')
      const surgeryTitle = this.getNestedProperty(invoice, 'surgery.title', '')
      return serviceTitle || surgeryTitle || this.formatInvoiceType(invoice?.type)
    },

    getNestedProperty(obj, path, defaultValue = 'N/A') {
      try {
        const result = path
          .split('.')
          .reduce(
            (acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined),
            obj,
          )
        return result !== undefined && result !== null ? result : defaultValue
      } catch {
        return defaultValue
      }
    },

    getPatientName(invoice) {
      return (
        this.getNestedProperty(
          invoice,
          'patient_card.patient.name',
          '-',
        ) || '-'
      )
    },

    getStatusVariant(status) {
      const variantMap = {
        pending: 'warning',
        approved: 'success',
        printed: 'info',
        rejected: 'danger',
        distributed: 'secondary',
        paid: 'success',
      }
      return variantMap[status] || 'secondary'
    },

    isUpdateDisabled(invoice) {
      return invoice.status === 'distributed'
    },

    openEditDoctorAmountModal(invoice) {
      if (this.isUpdateDisabled(invoice)) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: this.$t('accounting.cannot-update-distributed-invoice'),
          },
        })
        return
      }

      this.selectedInvoice = invoice
      this.doctorAmountForm.doctor_amount = parseFloat(
        invoice.doctor_amount || 0,
      )
      this.$bvModal.show(this.modalId)
    },

    closeEditDoctorAmountModal() {
      if (this.selectedInvoice) {
        this.$bvModal.hide(this.modalId)
      }
      this.selectedInvoice = null
      this.doctorAmountForm.doctor_amount = 0
    },

    async updateDoctorAmount() {
      if (!this.selectedInvoice || !this.selectedInvoice.id) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('accounting.invoice-not-selected'),
          },
        })
        return
      }

      if (this.isUpdateDisabled(this.selectedInvoice)) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: this.$t('accounting.cannot-update-distributed-invoice'),
          },
        })
        return
      }

      if (
        !this.doctorAmountForm.doctor_amount
        || this.doctorAmountForm.doctor_amount <= 0
      ) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('accounting.invalid-doctor-amount'),
          },
        })
        return
      }

      this.editingDoctorAmount = true

      try {
        const payload = {
          doctor_amount: parseFloat(this.doctorAmountForm.doctor_amount),
          doctor_id: this.doctorId,
        }

        await this.$http.put(
          `/invoices/${this.selectedInvoice.id}/update-doctor-amount`,
          payload,
        )

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('accounting.doctor-amount-updated-successfully'),
          },
        })

        // Close modal and refresh invoice list
        this.closeEditDoctorAmountModal()
        await this.getDoctorInvoices()
        await this.getDoctorStatusSummary()
      } catch (error) {
        console.error('Error updating doctor amount:', error)
        const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || this.$t('accounting.error-updating-doctor-amount')

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: errorMessage,
          },
        })
      } finally {
        this.editingDoctorAmount = false
      }
    },

    onTabChange(tabIndex) {
      this.activeTab = tabIndex
      this.selectedInvoices = []
      this.page = 1
      this.getDoctorInvoices()
      // Refresh status summary when tab changes
      this.getDoctorStatusSummary()
    },

    isInvoiceSelected(invoice) {
      return this.selectedInvoices.some(
        selected => selected.id === invoice.id,
      )
    },

    toggleInvoiceSelection(invoice) {
      const index = this.selectedInvoices.findIndex(
        selected => selected.id === invoice.id,
      )
      if (index > -1) {
        this.selectedInvoices.splice(index, 1)
      } else {
        this.selectedInvoices.push(invoice)
      }
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectedInvoices = [...this.invoicesData]
      } else {
        this.selectedInvoices = []
      }
    },

    clearSelection() {
      this.selectedInvoices = []
    },

    openPrintModal(statusType) {
      this.printModalStatusType = statusType
      this.showPrintModal = true
      this.$bvModal.show('modal-print-debt-statement')
    },

    closePrintModal() {
      this.showPrintModal = false
      this.invoicesToPrint = []
      this.autoPrintAfterUpdate = false
      this.$bvModal.hide('modal-print-debt-statement')
    },

    handlePrintModalShown() {
      // Auto-trigger print if this is after bulk update
      if (this.autoPrintAfterUpdate && this.$refs.debtStatementPrint) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.debtStatementPrint) {
              this.$refs.debtStatementPrint.generatePdf()
            }
          }, 500)
        })
      }
    },

    handlePrintStarted() {
      // Print has started
    },

    handlePrintCompleted() {
      // Close modal after print is completed
      setTimeout(() => {
        this.closePrintModal()
      }, 500)
    },

    async bulkUpdateStatusToPaid() {
      if (this.selectedInvoices.length === 0 || this.bulkUpdating) return

      this.bulkUpdating = true
      try {
        const invoicesPayload = this.selectedInvoices.map(invoice => ({
          id: invoice.id,
          statuses: ['approved', 'paid'],
        }))

        await this.$http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        // Store invoices to print (create copies with updated status)
        this.invoicesToPrint = this.selectedInvoices.map(invoice => ({
          ...invoice,
          status: 'paid',
        }))

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('accounting.invoices-marked-as-paid') || 'تم تحديد الفواتير كمدفوعة بنجاح',
          },
        })

        // Clear selection and refresh data
        const invoicesToPrintCopy = [...this.invoicesToPrint]
        this.clearSelection()
        await this.getDoctorInvoices()
        await this.getDoctorStatusSummary()

        // Open print modal with the invoices that were just marked as paid
        await this.$nextTick()
        this.printModalStatusType = 'paid'
        this.invoicesToPrint = invoicesToPrintCopy
        this.autoPrintAfterUpdate = true
        this.$bvModal.show('modal-print-debt-statement')
      } catch (error) {
        console.error('Error bulk updating status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.bulkUpdating = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
</style>
